import { useEffect, useRef, useState } from "react";
import { ActionList, ActionMenu, IconButton, Label, Text } from "@primer/react";
import {
  AlertIcon, PencilIcon, PlusIcon, StopIcon, TagIcon, TrashIcon
} from "@primer/octicons-react";
import { Alert } from "../components/Alert/Alert.jsx";
import { Button } from "../components/Button/Button.jsx";
import { ClearableTextInput } from "../components/ClearableTextInput/ClearableTextInput.jsx";
import { FormBody, FormContainer, FormFooter } from "../components/FormContainer/FormContainer.jsx";
import { revealInlineElement } from "../components/InlineEditor/revealInlineElement.js";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { propertyHref } from "../components/PropertyIdentity/PropertyIdentity.jsx";
import { propertyValueHref } from "../components/PropertyValueIdentity/PropertyValueIdentity.jsx";
import { Section, SectionStack } from "../components/Section/Section.jsx";
import { SortableList } from "../components/SortableList/SortableList.jsx";
import {
  countPropertyAssignments, countPropertyBrowserViewReferences, countPropertyPolicyReferences, countPropertyVisibilityCriteria,
  countValueAssignments, countValuePolicyReferences, countValueVisibilityCriteria, createProperty,
  deleteProperty, effectiveValue, updateProperty, validatePropertyDraft
} from "../domain/document.js";
import { navigateToHash } from "../lib/navigateToHash.js";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import "./PropertyPage.css";

export function PropertyPage({ propertyId, repository }) {
  const application = useApplicationDocument(repository);
  if (application.error && !application.document) return <Alert tone="danger" heading="Unable to open Regulator">{application.error.message}</Alert>;
  if (!application.document) return <PropertyLoading />;
  return <PropertyContent application={application} propertyId={propertyId} />;
}

function PropertyContent({ application, propertyId }) {
  const { document } = application;
  const isNew = propertyId === "new";
  const property = isNew ? null : document.properties.find((item) => item.id === propertyId);
  const [editing, setEditing] = useState(isNew);
  const [draft, setDraft] = useState(() => isNew ? { name: "", values: [] } : null);
  const [saveError, setSaveError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!isNew && !property) return <MissingProperty propertyId={propertyId} />;

  function startEditing() {
    setDraft({ id: property.id, name: property.name, values: property.values.map((value) => ({ ...value })) });
    setSaveError(null);
    setConfirmDelete(false);
    setEditing(true);
  }

  function cancelEditing() {
    if (isNew) {
      navigateToHash("#/properties");
      return;
    }
    setEditing(false);
    setDraft(null);
    setSaveError(null);
    setConfirmDelete(false);
  }

  async function saveProperty(nextDraft = draft) {
    setSaveError(null);
    try {
      const nextDocument = isNew ? createProperty(document, nextDraft) : updateProperty(document, property.id, nextDraft);
      if (await application.save(nextDocument)) {
        if (isNew) navigateToHash(propertyHref(nextDocument.properties.at(-1).id));
        else {
          setEditing(false);
          setDraft(null);
        }
      }
    } catch (error) {
      setSaveError(error.message);
    }
  }

  async function removeProperty() {
    if (await application.save(deleteProperty(document, property.id))) navigateToHash("#/properties");
  }

  const hasChanges = currentPropertyDraftHasChanges(document, draft, isNew ? "new" : property?.id);
  const valueCount = property?.values.length ?? 0;

  return <article className="property-page">
    <PageHeading
      description={property ? `${valueCount} possible value${valueCount === 1 ? "" : "s"}` : "Define a classification and its possible values."}
      actions={!editing ? <Button tone="secondary" leadingVisual={PencilIcon} onClick={startEditing} labelDetail="property">Edit</Button> : null}
    >{property?.name || "New property"}</PageHeading>
    {application.error ? <Alert tone="danger" heading="Changes were not saved">{application.error.message}</Alert> : null}
    {editing ? <PropertyEditor
      document={document}
      draft={draft}
      hasChanges={hasChanges}
      onChange={setDraft}
      onCancel={cancelEditing}
      onDelete={property ? () => setConfirmDelete(true) : null}
      onSave={saveProperty}
      saveError={saveError}
      saving={application.saving}
    /> : <SectionStack>
      <Section heading="Possible values" description="The first value is inherited when an asset has no explicit assignment.">
        <PropertyValues document={document} property={property} />
      </Section>
      <Section heading="Connections" description="Other records that depend on this property.">
        <PropertyConnections document={document} property={property} />
      </Section>
    </SectionStack>}
    {confirmDelete ? <DeletePropertyConfirmation
      assignmentCount={countPropertyAssignments(document, property.id)}
      policyCount={countPropertyPolicyReferences(document, property.id)}
      visibilityCount={countPropertyVisibilityCriteria(document, property.id)}
      viewCount={countPropertyBrowserViewReferences(document, property.id)}
      onCancel={() => setConfirmDelete(false)}
      onConfirm={removeProperty}
      property={property}
      saving={application.saving}
    /> : null}
  </article>;
}

function PropertyValues({ document, property }) {
  return <div className="property-values">{property.values.map((value, index) => {
    const assetCount = document.assets.filter((asset) => effectiveValue(document, asset.id, property.id).id === value.id).length;
    return <a className="property-value" href={propertyValueHref(property.id, value.id)} key={value.id}>
      <div className="property-value-name"><TagIcon aria-hidden="true" /><strong>{value.name}</strong>{index === 0 ? <Label variant="accent">Default</Label> : null}</div>
      <Text className="property-value-usage">{assetCount} asset{assetCount === 1 ? "" : "s"}</Text>
      <Text className="property-value-visibility">{visibilityDescription(document, value, index === 0)}</Text>
    </a>;
  })}</div>;
}

function visibilityDescription(document, value, isDefault) {
  if (isDefault) return "Inherited when no explicit value is assigned";
  if (!value.visibility) return "Always available";
  const property = document.properties.find((item) => item.id === value.visibility.propertyId);
  const condition = property?.values.find((item) => item.id === value.visibility.valueId);
  return property && condition ? `Available when ${property.name} is ${condition.name}` : "Availability condition is missing";
}

function PropertyConnections({ document, property }) {
  const connections = [
    [document.assets.length, "asset", "assets", "Receive an effective value"],
    [countPropertyPolicyReferences(document, property.id), "policy", "policies", "Reference this property"],
    [countPropertyBrowserViewReferences(document, property.id), "Browser view", "Browser views", "Group or filter by this property"],
    [countPropertyVisibilityCriteria(document, property.id), "dependent value", "dependent values", "Use this property for visibility"]
  ];
  return <dl className="property-connections">{connections.map(([count, singular, plural, description]) => <div key={singular}><dt>{count} {count === 1 ? singular : plural}</dt><dd>{description}</dd></div>)}</dl>;
}

function PropertyEditor({ document, draft, hasChanges, onCancel, onChange, onDelete, onSave, saveError, saving }) {
  const [pendingDelete, setPendingDelete] = useState(null);
  const [defaultChange, setDefaultChange] = useState(null);
  const [pendingFocusId, setPendingFocusId] = useState(null);
  const valueInputs = useRef(new Map());
  const savableDraft = { ...draft, values: draft.values.filter((value) => value.name?.trim()) };
  const validationError = validatePropertyDraft(savableDraft, document);
  const property = document.properties.find((item) => item.id === draft.id);
  const controlsVisibility = draft.id && document.properties.some((item) => (
    item.id !== draft.id && item.values.some((value) => value.visibility?.propertyId === draft.id)
  ));
  const hasVisibilityProperties = document.properties.some((item) => (
    item.id !== draft.id && item.values.every((value) => !value.visibility)
  ));

  function changeValue(index, name) {
    onChange({ ...draft, values: draft.values.map((value, valueIndex) => valueIndex === index ? { ...value, name } : value) });
  }

  useEffect(() => {
    if (!pendingFocusId) return;
    const input = valueInputs.current.get(pendingFocusId);
    if (input) {
      input.focus();
      setPendingFocusId(null);
    }
  }, [draft.values, pendingFocusId]);

  function addValue() {
    const value = { id: crypto.randomUUID(), name: "" };
    onChange({ ...draft, values: [...draft.values, value] });
    setPendingFocusId(value.id);
  }

  function handleValueKeyDown(event, index) {
    if (event.isComposing) return;
    if (event.key === "Escape" && !draft.values[index].name.trim()) {
      event.preventDefault();
      const nextFocusId = draft.values[index + 1]?.id ?? draft.values[index - 1]?.id;
      deleteValue(index);
      setPendingFocusId(nextFocusId ?? null);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const nextValue = draft.values[index + 1];
      if (nextValue) {
        valueInputs.current.get(nextValue.id)?.focus();
      } else {
        addValue();
      }
    }
  }

  function requestDeleteValue(index) {
    const value = draft.values[index];
    const propertyId = property?.id;
    const explicitCount = propertyId ? countValueAssignments(document, propertyId, value.id) : 0;
    const policyCount = propertyId ? countValuePolicyReferences(document, propertyId, value.id) : 0;
    const visibilityCount = propertyId ? countValueVisibilityCriteria(document, propertyId, value.id) : 0;
    const inheritedCount = index === 0 && propertyId
      ? document.assets.length - new Set(document.assignments.filter((item) => item.propertyId === propertyId).map((item) => item.assetId)).size
      : 0;
    if (explicitCount || inheritedCount || policyCount || visibilityCount) {
      setPendingDelete({ index, explicitCount, inheritedCount, policyCount, visibilityCount, value });
      revealInlineElement(`value-delete-${value.id}`, { focus: true });
    } else {
      deleteValue(index);
    }
  }

  function deleteValue(index) {
    onChange({ ...draft, values: draft.values.filter((_, valueIndex) => valueIndex !== index) });
    setPendingDelete(null);
  }

  function moveValue(fromIndex, toIndex) {
    if (!Number.isInteger(fromIndex) || toIndex < 0 || toIndex >= draft.values.length || fromIndex === toIndex) return;
    const values = [...draft.values];
    const [value] = values.splice(fromIndex, 1);
    values.splice(toIndex, 0, value);
    onChange({ ...draft, values });
  }

  function changeCriteria(index, visibility) {
    onChange({
      ...draft,
      values: draft.values.map((value, valueIndex) => {
        if (valueIndex !== index) return value;
        if (visibility) return { ...value, visibility };
        const { visibility: _visibility, ...unconditionedValue } = value;
        return unconditionedValue;
      })
    });
  }

  function requestSave() {
    if (property && property.values[0]?.id !== savableDraft.values[0]?.id) {
      const explicitlyAssignedAssets = new Set(document.assignments
        .filter((item) => item.propertyId === property.id)
        .map((item) => item.assetId));
      const inheritedCount = document.assets.filter((asset) => !explicitlyAssignedAssets.has(asset.id)).length;
      if (inheritedCount) {
        setDefaultChange({ inheritedCount, name: savableDraft.values[0]?.name });
        return;
      }
    }
    onSave(savableDraft);
  }

  return (
    <FormContainer className="property-editor">
      <FormBody>
        <label className="editor-name"><Text className="editor-label">Property name</Text><ClearableTextInput clearLabel="Clear property name" block value={draft.name} onClear={() => onChange({ ...draft, name: "" })} onChange={(event) => onChange({ ...draft, name: event.target.value })} onKeyDown={(event) => { if (event.key === "Enter" && !event.isComposing) { event.preventDefault(); addValue(); } }} autoFocus /></label>
        <div className="values-section">
          <Text className="editor-label">Possible values</Text>
          <SortableList
            className="compact value-editor-list"
            getId={(value) => value.id}
            getLabel={(value, index) => value.name || `value ${index + 1}`}
            itemClassName="value-editor"
            items={draft.values}
            onMove={moveValue}
            renderItem={(value, index) => (
              <div className="value-editor-entry">
                <div className="value-editor-content">
                  <ClearableTextInput
                    aria-label={`Value ${index + 1}`}
                    clearLabel={`Clear ${value.name || `value ${index + 1}`}`}
                    ref={(element) => {
                      if (element) valueInputs.current.set(value.id, element.querySelector?.("input") ?? element);
                      else valueInputs.current.delete(value.id);
                    }}
                    block
                    value={value.name}
                    onClear={() => changeValue(index, "")}
                    onChange={(event) => changeValue(index, event.target.value)}
                    onKeyDown={(event) => handleValueKeyDown(event, index)}
                  />
                  {index === 0 ? (
                    <div className="value-visibility default-visibility">
                      <Label className="default-value-label" variant="accent">Default</Label>
                      <Text>Visible: <strong>Always</strong></Text>
                    </div>
                  ) : (
                    <ValueVisibilityEditor
                      document={document}
                      disabled={Boolean(controlsVisibility) || !hasVisibilityProperties}
                      disabledReason={controlsVisibility ? "Properties used by other criteria cannot have criteria of their own." : !hasVisibilityProperties ? "Add another unconditional property before defining criteria." : undefined}
                      propertyId={draft.id}
                      value={value}
                      onChange={(visibility) => changeCriteria(index, visibility)}
                    />
                  )}
                  <IconButton className="value-delete" size="medium" variant="invisible" icon={TrashIcon} aria-label={`Delete ${value.name || `value ${index + 1}`}`} onClick={() => requestDeleteValue(index)} />
                </div>
                {pendingDelete?.value.id === value.id ? <DeleteValueConfirmation elementId={`value-delete-${value.id}`} pending={pendingDelete} onCancel={() => setPendingDelete(null)} onConfirm={() => deleteValue(index)} nextDefault={draft.values[1]?.name} /> : null}
              </div>
            )}
          />
          <div className="value-add-action"><Button tone="quiet" leadingVisual={PlusIcon} onClick={addValue}>Add value</Button></div>
        </div>
        {defaultChange ? (
          <div className="inline-confirmation">
            <Alert tone="warning" heading={`Make “${defaultChange.name}” the default?`}>
              {defaultChange.inheritedCount} asset{defaultChange.inheritedCount === 1 ? "" : "s"} will inherit this value.
            </Alert>
            <div><Button tone="quiet" onClick={() => setDefaultChange(null)}>Cancel</Button><Button onClick={() => onSave(savableDraft)}>Change default and save</Button></div>
          </div>
        ) : null}
      </FormBody>
      <FormFooter className="editor-footer" hasChanges={hasChanges}>
        <div className="editor-actions"><Button onClick={requestSave} disabled={!hasChanges || Boolean(validationError) || saving} labelDetail={saving ? undefined : "property"}>{saving ? "Saving…" : "Save"}</Button><Button tone="quiet" onClick={onCancel}>Cancel</Button></div>
        {saveError ? <EditorMessage tone="error"><StopIcon />{saveError}</EditorMessage> : null}
        {!saveError && validationError ? <EditorMessage tone="warning"><AlertIcon />{validationError}</EditorMessage> : null}
        {onDelete ? <Button className="form-danger-action" tone="danger" leadingVisual={TrashIcon} onClick={onDelete} labelDetail="property">Delete</Button> : null}
      </FormFooter>
    </FormContainer>
  );
}

function currentPropertyDraftHasChanges(document, draft, editingId) {
  if (!draft || !editingId) return false;
  if (editingId === "new") return draft.name !== "" || draft.values.length > 0;
  const property = document.properties.find((item) => item.id === editingId);
  return !property || JSON.stringify(draft) !== JSON.stringify({ id: property.id, name: property.name, values: property.values });
}

function EditorMessage({ children, tone }) {
  return <Text className={`editor-message ${tone}`} size="1" role={tone === "error" ? "alert" : "status"}>{children}</Text>;
}

function ValueVisibilityEditor({ disabled, disabledReason, document, onChange, propertyId, value }) {
  const availableProperties = document.properties.filter((property) => (
    property.id !== propertyId && property.values.every((item) => !item.visibility)
  ));
  const visibilityProperty = availableProperties.find((property) => property.id === value.visibility?.propertyId);
  const visibilityValue = visibilityProperty?.values.find((item) => item.id === value.visibility?.valueId);

  function selectMode(mode) {
    if (mode === "always") {
      onChange(null);
      return;
    }
    const property = visibilityProperty ?? availableProperties[0];
    if (property) onChange({ propertyId: property.id, valueId: property.values[0].id });
  }

  function selectProperty(property) {
    onChange({ propertyId: property.id, valueId: property.values[0].id });
  }

  return (
    <div className="value-visibility" title={disabled ? disabledReason : undefined}>
      <Text>Visible:</Text>
      <ActionMenu>
        <ActionMenu.Button className="menu-token visibility-token visibility-mode" disabled={disabled}>{value.visibility ? "When" : "Always"}</ActionMenu.Button>
        <ActionMenu.Overlay>
          <ActionList>
            <ActionList.Item onSelect={() => selectMode("always")}>Always</ActionList.Item>
            <ActionList.Item disabled={!availableProperties.length} onSelect={() => selectMode("when")}>When</ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {value.visibility && visibilityProperty ? (
        <>
          <ActionMenu>
            <ActionMenu.Button className="menu-token visibility-token">{visibilityProperty.name}</ActionMenu.Button>
            <ActionMenu.Overlay><ActionList>{availableProperties.map((property) => <ActionList.Item key={property.id} onSelect={() => selectProperty(property)}>{property.name}</ActionList.Item>)}</ActionList></ActionMenu.Overlay>
          </ActionMenu>
          <Text>is</Text>
          <ActionMenu>
            <ActionMenu.Button className="menu-token visibility-token">{visibilityValue?.name ?? visibilityProperty.values[0]?.name}</ActionMenu.Button>
            <ActionMenu.Overlay><ActionList>{visibilityProperty.values.map((item) => <ActionList.Item key={item.id} onSelect={() => onChange({ propertyId: visibilityProperty.id, valueId: item.id })}>{item.name}</ActionList.Item>)}</ActionList></ActionMenu.Overlay>
          </ActionMenu>
        </>
      ) : value.visibility ? (
        <ActionMenu>
          <ActionMenu.Button className="menu-token visibility-token">Property</ActionMenu.Button>
          <ActionMenu.Overlay><ActionList>{availableProperties.map((property) => <ActionList.Item key={property.id} onSelect={() => selectProperty(property)}>{property.name}</ActionList.Item>)}</ActionList></ActionMenu.Overlay>
        </ActionMenu>
      ) : null}
    </div>
  );
}

function DeleteValueConfirmation({ elementId, nextDefault, onCancel, onConfirm, pending }) {
  const inheritedMessage = pending.inheritedCount
    ? nextDefault
      ? `${pending.inheritedCount} asset${pending.inheritedCount === 1 ? "" : "s"} will inherit “${nextDefault}”.`
      : "Add a new possible value before saving this property."
    : pending.explicitCount ? "Affected assets will inherit the current default." : "";
  return (
    <div className="inline-confirmation value-delete-confirmation" id={elementId}>
      <Alert tone="warning" heading={`Delete “${pending.value.name}”?`}>
        {pending.explicitCount ? `${pending.explicitCount} explicit assignment${pending.explicitCount === 1 ? "" : "s"} will be removed. ` : ""}
        {pending.policyCount ? `${pending.policyCount} dependent polic${pending.policyCount === 1 ? "y" : "ies"} will be removed. ` : ""}
        {pending.visibilityCount ? `${pending.visibilityCount} visibility criteri${pending.visibilityCount === 1 ? "on" : "a"} will be removed, making the affected value${pending.visibilityCount === 1 ? "" : "s"} always visible. ` : ""}
        {inheritedMessage}
      </Alert>
      <div><Button tone="quiet" onClick={onCancel}>Cancel</Button><Button tone="danger" onClick={onConfirm} labelDetail="value">Delete</Button></div>
    </div>
  );
}

function DeletePropertyConfirmation({ assignmentCount, onCancel, onConfirm, policyCount, property, saving, viewCount, visibilityCount }) {
  return (
    <div className="inline-confirmation property-delete" id={`property-delete-${property.id}`}>
      <Alert tone="warning" heading={`Delete “${property.name}”?`}>
        This removes the property from every asset{assignmentCount ? `, deletes ${assignmentCount} explicit assignment${assignmentCount === 1 ? "" : "s"}` : ""}{policyCount ? `, removes ${policyCount} dependent polic${policyCount === 1 ? "y" : "ies"}` : ""}{viewCount ? `, updates ${viewCount} browser view${viewCount === 1 ? "" : "s"}` : ""}{visibilityCount ? `, and removes ${visibilityCount} visibility criteri${visibilityCount === 1 ? "on" : "a"}` : ""}.
      </Alert>
      <div><Button tone="quiet" onClick={onCancel}>Cancel</Button><Button tone="danger" onClick={onConfirm} disabled={saving} labelDetail={saving ? undefined : "property"}>{saving ? "Deleting…" : "Delete"}</Button></div>
    </div>
  );
}

function MissingProperty({ propertyId }) {
  return <article className="property-page"><PageHeading description="This property may have been deleted.">Property not found</PageHeading><Alert tone="warning" heading={`Property “${propertyId}” was not found`}><a href="#/properties">Return to Properties</a></Alert></article>;
}

function PropertyLoading() {
  return <article className="property-page"><PageHeading description="Loading property…">Property</PageHeading></article>;
}
